import { supabase } from "@/integrations/supabase/client";

export type ClientDocument = {
  id: string;
  client_id: string;
  file_path: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  visible_to_client: boolean;
  uploaded_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

const BUCKET = "client-documents";

export const listClientDocuments = async (clientId: string): Promise<ClientDocument[]> => {
  const { data, error } = await supabase
    .from("client_documents" as any)
    .select("*")
    .eq("client_id", clientId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as unknown as ClientDocument[]) || [];
};

export const uploadClientDocument = async (
  clientId: string,
  file: File,
  opts: { visibleToClient: boolean; notes?: string }
): Promise<ClientDocument> => {
  const cleanName = file.name.replace(/[^\w.\-]+/g, "_");
  const path = `${clientId}/${Date.now()}_${cleanName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (uploadError) throw uploadError;

  const { data: userRes } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from("client_documents" as any)
    .insert({
      client_id: clientId,
      file_path: path,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type,
      visible_to_client: opts.visibleToClient,
      uploaded_by: userRes.user?.id ?? null,
      notes: opts.notes ?? null,
    })
    .select()
    .single();

  if (error) {
    await supabase.storage.from(BUCKET).remove([path]).catch(() => {});
    throw error;
  }
  return data as unknown as ClientDocument;
};

export const setDocumentVisibility = async (id: string, visible: boolean) => {
  const { error } = await supabase
    .from("client_documents" as any)
    .update({ visible_to_client: visible })
    .eq("id", id);
  if (error) throw error;
};

export const deleteClientDocument = async (doc: ClientDocument) => {
  await supabase.storage.from(BUCKET).remove([doc.file_path]).catch(() => {});
  const { error } = await supabase
    .from("client_documents" as any)
    .delete()
    .eq("id", doc.id);
  if (error) throw error;
};

export const getSignedDocumentUrl = async (path: string, expiresIn = 300): Promise<string> => {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, expiresIn);
  if (error) throw error;
  return data.signedUrl;
};

/** For the customer-facing "Mes réservations" page. RLS filters automatically. */
export const listVisibleDocumentsForEmail = async (email: string): Promise<ClientDocument[]> => {
  const { data: client } = await supabase
    .from("clients")
    .select("id")
    .ilike("email", email)
    .maybeSingle();
  if (!client) return [];
  const { data, error } = await supabase
    .from("client_documents" as any)
    .select("*")
    .eq("client_id", client.id)
    .eq("visible_to_client", true)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as unknown as ClientDocument[]) || [];
};
